import React, { useState } from 'react';
import { Clock, Download, Search, User, FileText, Mail, Tag, Users, Settings, Send, ChevronDown, ChevronRight } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
const actionColors: Record<string, {
  bg: string;
  text: string;
}> = {
  created: {
    bg: 'bg-green-100',
    text: 'text-green-800'
  },
  updated: {
    bg: 'bg-blue-100',
    text: 'text-blue-800'
  },
  deleted: {
    bg: 'bg-red-100',
    text: 'text-red-800'
  },
  status_changed: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800'
  },
  duplicated: {
    bg: 'bg-purple-100',
    text: 'text-purple-800'
  },
  archived: {
    bg: 'bg-gray-100',
    text: 'text-gray-800'
  },
  restored: {
    bg: 'bg-teal-100',
    text: 'text-teal-800'
  }
};
const entityIcons: Record<string, any> = {
  form: FileText,
  template: Mail,
  newsletter: Mail,
  campaign: Send,
  user: User,
  tag: Tag,
  form_config: Settings,
  workspace: Users,
  subscription: Users,
  submission: FileText,
  contact: Users,
  role: Settings
};
export function AuditLog() {
  const {
    auditEvents
  } = useWorkspace();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAction, setFilterAction] = useState<string>('all');
  const [filterEntity, setFilterEntity] = useState<string>('all');
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const toggleExpanded = (id: number) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedEvents(newExpanded);
  };
  const filteredEvents = auditEvents.filter(event => {
    const matchesSearch = event.entityName.toLowerCase().includes(searchQuery.toLowerCase()) || event.userName.toLowerCase().includes(searchQuery.toLowerCase()) || event.userEmail.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAction = filterAction === 'all' || event.action === filterAction;
    const matchesEntity = filterEntity === 'all' || event.entityType === filterEntity;
    return matchesSearch && matchesAction && matchesEntity;
  });
  const uniqueActions = [...new Set(auditEvents.map(e => e.action))];
  const uniqueEntities = [...new Set(auditEvents.map(e => e.entityType))];
  return <div className="space-y-6" data-id="element-1362">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1363">
        <div className="min-w-0" data-id="element-1364">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1365">Audit Log</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1366">
            Complete history of all changes made in your workspace.
          </p>
        </div>
        <button className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 whitespace-nowrap flex-shrink-0" data-id="element-1367">
          <Download className="h-4 w-4 flex-shrink-0" data-id="element-1368" />
          <span data-id="element-1369">Export CSV</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-1370">
        <div className="relative flex-1 min-w-0" data-id="element-1371">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-1372" />
          <input type="text" placeholder="Search by entity name, user..." className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1373" />
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap sm:flex-nowrap" data-id="element-1374">
          <select className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 flex-shrink-0" value={filterAction} onChange={e => setFilterAction(e.target.value)} data-id="element-1375">
            <option value="all" data-id="element-1376">All Actions</option>
            {uniqueActions.map(action => <option key={action} value={action} data-id="element-1377">
                {action.replace('_', ' ')}
              </option>)}
          </select>
          <select className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 flex-shrink-0" value={filterEntity} onChange={e => setFilterEntity(e.target.value)} data-id="element-1378">
            <option value="all" data-id="element-1379">All Entities</option>
            {uniqueEntities.map(entity => <option key={entity} value={entity} data-id="element-1380">
                {entity.replace('_', ' ')}
              </option>)}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-1381">
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-1382">
          <p className="text-2xl font-semibold text-gray-900" data-id="element-1383">
            {auditEvents.length}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-1384">Total Events</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-1385">
          <p className="text-2xl font-semibold text-green-600" data-id="element-1386">
            {auditEvents.filter(e => e.action === 'created').length}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-1387">Created</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-1388">
          <p className="text-2xl font-semibold text-blue-600" data-id="element-1389">
            {auditEvents.filter(e => e.action === 'updated').length}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-1390">Updated</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-1391">
          <p className="text-2xl font-semibold text-red-600" data-id="element-1392">
            {auditEvents.filter(e => e.action === 'deleted').length}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-1393">Deleted</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 col-span-2 sm:col-span-1" data-id="element-1394">
          <p className="text-2xl font-semibold text-yellow-600" data-id="element-1395">
            {auditEvents.filter(e => e.action === 'status_changed').length}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-1396">Status Changed</p>
        </div>
      </div>

      {/* Events List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1397">
        {filteredEvents.length === 0 ? <div className="p-12 text-center" data-id="element-1398">
            <Clock className="mx-auto h-12 w-12 text-gray-400" data-id="element-1399" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-1400">
              No events found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-1401">
              Try adjusting your search or filters.
            </p>
          </div> : <div className="divide-y divide-gray-200" data-id="element-1402">
            {filteredEvents.map(event => {
          const EntityIcon = entityIcons[event.entityType] || FileText;
          const actionStyle = actionColors[event.action] || actionColors.updated;
          const isExpanded = expandedEvents.has(event.id);
          const hasDetails = event.changes || event.metadata;
          return <div key={event.id} className="hover:bg-gray-50" data-id="element-1403">
                  <div className={`p-4 flex items-start gap-3 sm:gap-4 ${hasDetails ? 'cursor-pointer' : ''}`} onClick={() => hasDetails && toggleExpanded(event.id)} data-id="element-1404">
                    {/* Icon */}
                    <div className={`flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center ${actionStyle.bg}`} data-id="element-1405">
                      <EntityIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${actionStyle.text}`} data-id="element-1406" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0" data-id="element-1407">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-1408">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${actionStyle.bg} ${actionStyle.text}`} data-id="element-1409">
                          {event.action.replace('_', ' ')}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 whitespace-nowrap" data-id="element-1410">
                          {event.entityType.replace('_', ' ')}
                        </span>
                        <span className="text-sm font-medium text-gray-900 truncate" data-id="element-1411">
                          {event.entityName}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-sm text-gray-500 flex-wrap" data-id="element-1412">
                        <span className="flex items-center gap-1 whitespace-nowrap" data-id="element-1413">
                          <User className="h-3 w-3 flex-shrink-0" data-id="element-1414" />
                          <span className="truncate max-w-[100px] sm:max-w-none" data-id="element-1415">
                            {event.userName}
                          </span>
                        </span>
                        <span className="hidden sm:inline" data-id="element-1416">•</span>
                        <span className="hidden sm:inline truncate" data-id="element-1417">
                          {event.userEmail}
                        </span>
                      </div>
                    </div>

                    {/* Timestamp & Expand */}
                    <div className="flex items-center gap-2 text-right flex-shrink-0" data-id="element-1418">
                      <div className="hidden sm:block" data-id="element-1419">
                        <div className="text-sm text-gray-900 whitespace-nowrap" data-id="element-1420">
                          {event.timestamp}
                        </div>
                        <div className="text-xs text-gray-500 font-mono" data-id="element-1421">
                          {event.ipAddress}
                        </div>
                      </div>
                      <div className="sm:hidden text-xs text-gray-500 whitespace-nowrap" data-id="element-1422">
                        {event.timestamp.split(' ')[0]}
                      </div>
                      {hasDetails && <div className="text-gray-400 flex-shrink-0" data-id="element-1423">
                          {isExpanded ? <ChevronDown className="h-4 w-4" data-id="element-1424" /> : <ChevronRight className="h-4 w-4" data-id="element-1425" />}
                        </div>}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && hasDetails && <div className="px-4 pb-4 ml-11 sm:ml-14 border-l-2 border-gray-100" data-id="element-1426">
                      {event.changes && event.changes.length > 0 && <div className="bg-gray-50 rounded-md p-3 space-y-2" data-id="element-1427">
                          <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-1428">
                            Changes
                          </p>
                          {event.changes.map((change, idx) => <div key={idx} className="flex items-center gap-2 text-sm flex-wrap" data-id="element-1429">
                              <span className="font-medium text-gray-700" data-id="element-1430">
                                {change.field}:
                              </span>
                              <span className="text-red-600 line-through" data-id="element-1431">
                                {change.oldValue || '(empty)'}
                              </span>
                              <span className="text-gray-400" data-id="element-1432">→</span>
                              <span className="text-green-600" data-id="element-1433">
                                {change.newValue || '(empty)'}
                              </span>
                            </div>)}
                        </div>}
                      {event.metadata && <div className="bg-gray-50 rounded-md p-3 mt-2" data-id="element-1434">
                          <p className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-1435">
                            Additional Info
                          </p>
                          <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap break-all" data-id="element-1436">
                            {JSON.stringify(event.metadata, null, 2)}
                          </pre>
                        </div>}
                    </div>}
                </div>;
        })}
          </div>}
      </div>
    </div>;
}